// ═══════════════════════════════════════════════════════════════════════════
// Canonical subselector resolver for DXF-sourced associated entity refs.
// ═══════════════════════════════════════════════════════════════════════════
//
// CONTEXT
// ───────
// AutoCAD's constraint engine references geometry features by a pair of
// persistent subent-IDs (read from ACDBASSOCGEOMDEPENDENCY records). On
// LWPOLYLINE entities each vertex carries a persistent integer (DXF
// group 91 inside AcDbPolyline body); the constraint's `subentIdA`/`B`
// are two of those vertex IDs that name an edge between them.
//
// The solver integration layer (`constraintSolverIntegration`) does not
// speak persistent IDs — it wants canonical subselector strings:
//   "start" / "end"   — Line endpoints
//   "center"          — Circle / Arc / Ellipse center
//   "v<i>"            — LWPOLYLINE vertex by index (legacy fallback)
//   "edge<i>"         — LWPOLYLINE edge starting at vertex i
//   ""                — whole-entity (Line, block-anchor, etc.)
//
// This helper translates a DXF-sourced (entity, subentType, subentIdA,
// subentIdB, isSingleEdge) tuple into a canonical subselector. The
// canonical form is what AutoCAD's Inspector / DIMCONSTRAINT dialogs
// display and what the LM solver's residual functions can look up
// without ambiguity.
//
// DISCRIMINATOR
// ─────────────
// `isSingleEdge` is captured by the assocdeps parser from the PersSubentId
// subclass name:
//   - AcDbAssocEdgePersSubentId       → isSingleEdge = false
//       (LWPOLYLINE edge between two 90-code vertex IDs)
//   - AcDbAssocSingleEdgePersSubentId → isSingleEdge = true
//       (whole LINE / CIRCLE / ARC — no vertex-pair)
//
// Forward-compatible `subentType` is reserved for AutoCAD 2024+ Face /
// Region subent classes; Phase 1 only supports subentType = 0 (edge).

/**
 * Duck-typed entity shape this resolver reads from. Mirrors the fields
 * `constraintCollection` already uses via the block-evaluator
 * `EntityLike` type — we keep the shape narrow so the helper is
 * reusable from BlockChildEntity and from the top-level drawing entity
 * table alike.
 */
export interface SubselectorResolverEntity {
  readonly type: string;
  readonly vertices?: ReadonlyArray<{
    readonly x: number;
    readonly y: number;
    readonly vertexId?: number;
  }>;
}

/**
 * Translate a DXF-sourced associated-entity ref to AutoCAD's canonical
 * subselector label.
 *
 * Behaviour per entity type:
 *
 * Single-edge (isSingleEdge = true):
 *   - Line                : "" (whole-line anchor; caller falls back to
 *                           coordinate match for "start"/"end")
 *   - Circle/Arc/Ellipse  : "center"
 *   - LWPOLYLINE          : "" (whole-polyline anchor — unusual; callers
 *                           usually see Edge form for polylines)
 *   - Anything else       : ""
 *
 * Edge form (isSingleEdge = false), LWPOLYLINE:
 *   - Both `subentIdA` and `subentIdB` resolve to vertex indices
 *       → "edge<min(idxA, idxB)>"
 *   - Only `subentIdA` resolves
 *       → "v<idxA>"
 *   - Neither resolves (legacy fixture with no persistent IDs)
 *       → "v<subentIdA>" (legacy fallback — keeps existing behaviour)
 *
 * Edge form, non-LWPOLYLINE:
 *   - "v<subentIdA>" (legacy fallback — mirrors pre-Phase-1 placeholder)
 *
 * The caller is responsible for treating empty-string subselectors as
 * whole-entity anchors and for the coordinate-match fall-through in
 * that case (see constraintSolverIntegration.resolvePointRef).
 *
 * @param entity       — the target entity (found by ref.entityId)
 * @param subentType   — DXF 90 code after PersSubentId marker; only
 *                       `0` (edge) is supported in Phase 1. Non-zero
 *                       values are forwarded to the Edge-form branch.
 * @param subentIdA    — first vertex subent ID (90 code), 0 for Single
 * @param subentIdB    — second vertex subent ID (90 code), 0 for Single
 * @param isSingleEdge — parser-captured discriminator between the two
 *                       PersSubentId subclass forms
 */
export function resolveSubselector(
  entity: SubselectorResolverEntity,
  subentType: number,
  subentIdA: number,
  subentIdB: number,
  isSingleEdge: boolean,
): string {
  void subentType; // Phase 1: only subentType=0 (edge) is defined; reserved.

  if (isSingleEdge) {
    switch (entity.type) {
      case "Line":
        return "";
      case "Circle":
      case "Arc":
      case "Ellipse":
        return "center";
      case "LWPOLYLINE":
        return "";
      default:
        return "";
    }
  }

  // Edge form — translate the vertex-pair subent IDs to a canonical
  // edge subselector for LWPOLYLINE. For other entity types the pair is
  // not meaningful; we fall through to the legacy `v<subentIdA>` hook.
  if (entity.type === "LWPOLYLINE") {
    const verts = entity.vertices ?? [];
    const idxA = verts.findIndex((v) => v.vertexId === subentIdA);
    const idxB = verts.findIndex((v) => v.vertexId === subentIdB);
    if (idxA >= 0 && idxB >= 0) {
      return `edge${Math.min(idxA, idxB)}`;
    }
    if (idxA >= 0) {
      return `v${idxA}`;
    }
    // Legacy fixture with no persistent vertex IDs — keep the placeholder
    // subselector that downstream consumers have been tolerating.
    return `v${subentIdA}`;
  }

  return `v${subentIdA}`;
}
